import {
  Ellipsis,
  Lightbulb,
  LightbulbOff,
  Lock,
  Shield,
  Sparkles,
  User,
  UserPen,
  UserX,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { OwnerSearchPopover } from "@/components/owner-search-popover";
import { Button } from "@/components/ui/button";
import { ChangeIndicatorBadge } from "@/components/ui/change-indicator-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  combineQualityGuardIssueState,
  QUALITY_GUARD_OPEN_WORK_GROUP_KEY,
  QualityGuardAcceptedRiskBadge,
  QualityGuardIssueBadge,
  QualityGuardOpenWorkTag,
} from "@/features/project/components/quality-guard/issue-badge";
import { resolveUser, useOrgUserMap } from "@/hooks";
import { cn } from "@/lib/utils";
import type { DiffNodeStatus, QualityGuardFieldIssues } from "../types";

interface FieldHeaderProps {
  fieldId?: string;
  label: string;
  guidance?: string;
  expectedMentionTypes?: string[];
  diffStatus?: DiffNodeStatus;
  ownerId?: string | null;
  entityOwnerId?: string | null;
  showOwner?: boolean;
  showGuidanceCallout?: boolean;
  showMeatball?: boolean;
  showGuardPlaceholder?: boolean;
  qualityGuardIssues?: QualityGuardFieldIssues | null;
  canEditOwner?: boolean;
  onOwnerChange?: (ownerId: string | null) => void;
  onToggleGuidance?: () => void;
  onAskClaudia?: (args: {
    fieldId: string;
    label: string;
    guidance?: string;
    expectedMentionTypes?: string[];
  }) => void;
  onOpenQualityGuard?: (args: {
    fieldId: string;
    label: string;
    qualityGuardGroupKey?: string;
  }) => void;
  className?: string;
  titleClassName?: string;
  ownerClassName?: string;
}

function QualityGuardFieldIssueChips({
  label,
  fieldId,
  issues,
  onOpenQualityGuard,
}: {
  label: string;
  fieldId?: string;
  issues?: QualityGuardFieldIssues | null;
  onOpenQualityGuard?: (args: {
    fieldId: string;
    label: string;
    qualityGuardGroupKey?: string;
  }) => void;
}) {
  if (!issues) {
    return null;
  }

  const combinedIssueState = combineQualityGuardIssueState(issues);
  const chips = (
    <span className="inline-flex items-center gap-2">
      <QualityGuardIssueBadge
        label={`Quality Guard for ${label}`}
        count={combinedIssueState.count}
        severity={combinedIssueState.severity}
        checking={issues.checking}
        evaluationState={issues.evaluationState}
        evaluationCounts={issues.evaluationCounts}
      />
      <QualityGuardAcceptedRiskBadge
        label={`Accepted risks for ${label}`}
        count={issues.acceptedRiskCount}
      />
    </span>
  );

  // Its own control, outside the issue badges' button and separated from them by a wider
  // gap: a section can be clean on every issue axis and still owe work, so the two must
  // read as two answers rather than as one strip of severity.
  const openWork = (
    <QualityGuardOpenWorkTag
      count={issues.openWorkCount}
      summaries={issues.openWorkSummaries}
      {...(fieldId && onOpenQualityGuard
        ? {
            onClick: () =>
              onOpenQualityGuard({
                fieldId,
                label,
                qualityGuardGroupKey: QUALITY_GUARD_OPEN_WORK_GROUP_KEY,
              }),
          }
        : {})}
    />
  );

  if (!fieldId || !onOpenQualityGuard) {
    return (
      <span className="inline-flex items-center gap-3">
        {chips}
        {openWork}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-3">
      <button
        type="button"
        className="inline-flex rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Open Quality Guard issues for ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          onOpenQualityGuard({ fieldId, label });
        }}
      >
        {chips}
      </button>
      {openWork}
    </span>
  );
}

export function FieldHeader({
  fieldId,
  label,
  guidance,
  expectedMentionTypes,
  diffStatus = null,
  ownerId,
  entityOwnerId,
  showOwner = true,
  showGuidanceCallout = false,
  showMeatball = false,
  showGuardPlaceholder = false,
  qualityGuardIssues,
  canEditOwner = false,
  onOwnerChange,
  onToggleGuidance,
  onAskClaudia,
  onOpenQualityGuard,
  className,
  titleClassName,
  ownerClassName,
}: FieldHeaderProps) {
  const userMap = useOrgUserMap();
  const [ownerPopoverOpen, setOwnerPopoverOpen] = useState(false);
  const resolvedOwnerId = ownerId ?? entityOwnerId ?? null;
  const resolvedOwner = resolveUser(userMap, resolvedOwnerId);
  const ownerDisplay = resolvedOwnerId ? resolvedOwner.fullName : "Unassigned";
  const canChangeOwner = canEditOwner && typeof onOwnerChange === "function";
  const canResetOwner = ownerId !== undefined && ownerId !== null;
  const shouldShowOwner = showOwner || ownerPopoverOpen;
  const showQualityGuardIssues = Boolean(qualityGuardIssues);

  const ownerButton = useMemo(
    () => (
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 rounded-sm text-[12px] font-normal text-neutral-dim transition-colors hover:text-foreground focus-visible:ring-ring/40 focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-70",
          ownerClassName
        )}
      >
        <User className="size-3 text-neutral-dim" />
        <span className="max-w-40 truncate">{ownerDisplay}</span>
      </button>
    ),
    [ownerClassName, ownerDisplay]
  );

  return (
    <div className={cn("space-y-2.5", className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            data-df-section-header-label=""
            data-field-id={fieldId}
            data-label={label}
            className={cn("text-base font-semibold text-foreground", titleClassName)}
          >
            {label}
          </span>
          {diffStatus ? <ChangeIndicatorBadge /> : null}
          {showQualityGuardIssues ? (
            <QualityGuardFieldIssueChips
              fieldId={fieldId}
              label={label}
              issues={qualityGuardIssues}
              onOpenQualityGuard={onOpenQualityGuard}
            />
          ) : showGuardPlaceholder ? (
            <Shield className="size-3.5 text-neutral-dim" />
          ) : null}
          {showMeatball ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6 text-neutral-dim hover:text-foreground"
                    aria-label={`Open actions for ${label}`}
                  />
                }
              >
                <Ellipsis className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {onToggleGuidance ? (
                  <DropdownMenuItem onClick={onToggleGuidance}>
                    {showGuidanceCallout ? (
                      <>
                        <LightbulbOff className="size-3.5" />
                        Hide guidance
                      </>
                    ) : (
                      <>
                        <Lightbulb className="size-3.5" />
                        Show guidance
                      </>
                    )}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  onClick={() => {
                    if (canChangeOwner) {
                      setOwnerPopoverOpen(true);
                    }
                  }}
                >
                  <UserPen className="size-3.5" />
                  Assign owner
                </DropdownMenuItem>
                {canResetOwner ? (
                  <DropdownMenuItem onClick={() => onOwnerChange?.(null)}>
                    <UserX className="size-3.5" />
                    Reset owner
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {}}>
                  <Lock className="size-3.5" />
                  Sign off and lock
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!fieldId || !onAskClaudia) {
                      return;
                    }
                    onAskClaudia({
                      fieldId,
                      label,
                      guidance,
                      expectedMentionTypes,
                    });
                  }}
                >
                  <Sparkles className="size-3.5" />
                  Ask Claudia
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        {shouldShowOwner ? (
          <div className="shrink-0">
            {canChangeOwner ? (
              <Popover open={ownerPopoverOpen} onOpenChange={setOwnerPopoverOpen}>
                <PopoverTrigger render={ownerButton} />
                <PopoverContent align="end" className="w-80 p-0">
                  <OwnerSearchPopover
                    userMap={userMap}
                    onSelect={(nextOwnerId) => {
                      onOwnerChange?.(nextOwnerId);
                      setOwnerPopoverOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              ownerButton
            )}
          </div>
        ) : null}
      </div>

      <AnimatePresence initial={false}>
        {showGuidanceCallout && guidance ? (
          <motion.div
            key="field-guide"
            data-field-guide=""
            contentEditable={false}
            draggable={false}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
            style={{ overflow: "hidden" }}
            className="document-field-chrome group/guide"
          >
            <div className="flex items-start gap-1.5 py-1 text-[12px] leading-normal text-neutral-dim">
              {onToggleGuidance ? (
                <button
                  type="button"
                  onClick={onToggleGuidance}
                  className="mt-0.5 shrink-0 text-neutral-dim hover:text-foreground"
                  aria-label="Hide guidance"
                >
                  <Lightbulb className="block size-3.5 group-hover/guide:hidden" />
                  <LightbulbOff className="hidden size-3.5 group-hover/guide:block" />
                </button>
              ) : (
                <Lightbulb className="mt-0.5 size-3.5 shrink-0 text-neutral-dim" />
              )}
              <p>{guidance}</p>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
